import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import ModalErrorMessage from '../../components/modalErrorMessage/modalErrorMessage';
import Loading from '../../components/loading';
import ModalSuccessMessage from '../../components/modalSuccessMessage/modalSuccessMessage';
import { useIncomeCategories } from '../hooks/useIncomeCategories';
import { useSaveIncome } from '../hooks/useSaveIncome';
import { useIncome } from '../hooks/useIncome';

export default function IncomesModal({
  open,
  onClose,
  onSaveSuccess,
  incomeId,
  incomeDescription,
  incomeAmount,
  incomeOrigin,
  patientId,
  professionalId,
}) {
  const [receivedDate, setReceivedDate] = useState(null);
  const [receiptMethodType, setReceiptMethodType] = useState('');
  const [dueDate, setDueDate] = useState(dayjs());
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [isReceived, setIsReceived] = useState(false);
  const [installments, setInstallments] = useState('');
  const [hasInstallments, setHasInstallments] = useState(false);
  const [observation, setObservation] = useState('');

  const [categoryId, setCategoryId] = useState('');
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const [isSaving, setIsSaving] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState('');

  const { categories, refetch: refetchCategories, saveCategory } = useIncomeCategories();
  const { save: saveIncomeRequest } = useSaveIncome();
  const { income, isLoading: isIncomeLoading } = useIncome(incomeId);

  const isEditing = Boolean(incomeId);
  const isLoading = isSaving || isIncomeLoading;

  const clearForm = () => {
    setReceivedDate(null);
    setDueDate(dayjs());
    setAmount('');
    setDescription('');
    setIsReceived(false);
    setInstallments('');
    setHasInstallments(false);
    setObservation('');
    setErrorMessage('');
    setCategoryId('');
    setReceiptMethodType('');
  };

  useEffect(() => {
    if (!incomeId) {
      clearForm();
    }
    setDescription(incomeDescription ?? '');
    setAmount(incomeAmount ?? 0);
  }, [incomeId, incomeDescription, incomeAmount]);

  useEffect(() => {
    if (!income) {
      return;
    }
    setDueDate(dayjs(income.dueDate, 'YYYY-MM-DD'));
    setAmount(income.amount);
    if (income.receivedDate) {
      setIsReceived(true);
      setReceivedDate(dayjs(income.receivedDate, 'YYYY-MM-DD'));
      setReceiptMethodType(income.receiptMethodType);
    }
    if (income.installments > 0) {
      setInstallments(income.installments);
      setHasInstallments(true);
    }
    setCategoryId(income.categoryId);
    setDescription(income.description);
    setObservation(income.observation ?? '');
  }, [income]);

  const saveNewCategory = () => {
    const categoryToSave = {
      description: newCategoryName,
    };
    setIsSaving(true);
    setOpenCategoryModal(false);
    saveCategory(categoryToSave)
      .then((returnFromApi) => {
        refetchCategories();
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
      })
      .catch((returnFromApi) => {
        console.error('ops! ocorreu um erro' + returnFromApi);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const saveIncome = () => {
    setErrorMessage('');

    if (isReceived) {
      if (!receiptMethodType || !receivedDate) {
        setShowErrorMessage(true);
        setErrorMessage('É obrigatório selecionar a data e o tipo de Recebimento.');
        return;
      }
    }

    if (!amount || !dueDate || !categoryId || !description) {
      setShowErrorMessage(true);
      setErrorMessage('É obrigatório selecionar Data, Valor, Categoria e Descrição.');
      return;
    }

    const incomePayload = {
      id: incomeId,
      categoryId: categoryId,
      dueDate: dueDate.format('YYYY-MM-DD'),
      receivedDate: receivedDate ? receivedDate.format('YYYY-MM-DD') : null,
      amount: parseFloat(amount),
      description: description,
      isReceived: isReceived,
      installments: hasInstallments ? installments : null,
      observation: observation,
      receiptMethodType: receiptMethodType,
      origin: incomeOrigin,
      patientId: patientId,
      professionalId: professionalId,
    };

    setIsSaving(true);
    saveIncomeRequest(incomePayload)
      .then((returnFromApi) => {
        clearForm();
        onSaveSuccess(returnFromApi);
      })
      .catch((returnFromApi) => {
        console.error('ops! ocorreu um erro' + returnFromApi);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Nova Entrada</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Loading isToHidden={isLoading === false} />
            {showSuccessMessage && (
              <ModalSuccessMessage
                message={successMessage}
                show={showSuccessMessage}
                setShow={setShowSuccessMessage}
              />
            )}
            {showErrorMessage && (
              <ModalErrorMessage
                message={errorMessage}
                show={showErrorMessage}
                setShow={setShowErrorMessage}
              />
            )}

            <Grid size={6}>
              <DatePicker
                label="Data de Vencimento"
                value={dueDate}
                onChange={(newValue) => setDueDate(newValue)}
                sx={{ width: '100%' }}
                format="DD/MM/YYYY"
              />
            </Grid>
            <Grid size={6}>
              <TextField
                data-cy="txt-value"
                label={hasInstallments ? 'Valor da parcela (R$)' : 'Valor (R$)'}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasInstallments}
                    disabled={isEditing}
                    onChange={(e) => {
                      setHasInstallments(e.target.checked);
                      if (e.target.checked) setIsReceived(false);
                    }}
                  />
                }
                label="Com parcelamento"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isReceived}
                    disabled={hasInstallments}
                    onChange={(e) => {
                      setIsReceived(e.target.checked);
                      if (e.target.checked && !receivedDate) {
                        setReceivedDate(dayjs());
                      }
                    }}
                  />
                }
                label="Marcar como recebida"
              />
            </Grid>

            {hasInstallments && !isReceived && (
              <Grid size={12}>
                <TextField
                  disabled={isEditing}
                  label="Quantidade de parcelas"
                  type="number"
                  value={installments}
                  onChange={(e) => setInstallments(e.target.value)}
                  fullWidth
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
            )}

            {isReceived && (
              <>
                <Grid size={12}>
                  <DatePicker
                    label="Data do Recebimento"
                    value={receivedDate}
                    onChange={(newValue) => setReceivedDate(newValue)}
                    sx={{ width: '100%' }}
                    format="DD/MM/YYYY"
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    select
                    label="Tipo de Recebimento"
                    value={receiptMethodType}
                    onChange={(e) => setReceiptMethodType(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="PIX">Pix</MenuItem>
                    <MenuItem value="CREDIT_CARD">Cartão de Crédito</MenuItem>
                    <MenuItem value="CASH">Dinheiro</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            <Grid size={12}>
              <Grid container spacing={1} alignItems="center">
                <Grid size="grow">
                  <TextField
                    data-cy="select-category"
                    select
                    label="Categoria"
                    fullWidth
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                  >
                    {categories.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.description}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size="auto">
                  <Button variant="outlined" onClick={() => setOpenCategoryModal(true)}>
                    Nova Categoria
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <TextField
                data-cy="txt-description"
                label="Descrição"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid size={12}>
              <TextField
                data-cy="txt-observation"
                label="Observação"
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button data-cy="btn-save" onClick={saveIncome} variant="contained" disabled={isLoading}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCategoryModal}
        onClose={() => setOpenCategoryModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Nova Categoria</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome da Categoria"
            fullWidth
            variant="outlined"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryModal(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={saveNewCategory} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
